'use client';

/**
 * Cross-cutting Share dialog. Renders a centered modal on desktop and a
 * bottom sheet on mobile (capped at 90vh). Drives every share affordance
 * in the product (My Library row + selection bar, folders, canvas content
 * node, workspace top-bar).
 *
 * Shape: `kind === 'content'` accepts an array of `contentIds` so
 * single-item and bulk-selection callers share the same component;
 * `kind === 'workspace'` accepts a single workspaceId. All visibility
 * and grant ops are applied to the entire selection on Save.
 *
 * EMAIL INVITATIONS (ADR-0036) split across that Save contract, deliberately.
 * MINTING does not wait: the grants route hands the plaintext link back exactly
 * once, and when the mail rail is on it queues a message that cannot be
 * un-sent, so there is nothing a Cancel could take back. WITHDRAWING does wait,
 * like every other removal in the same list. It returns nothing and sends
 * nothing, so the only thing immediacy bought was two trash icons in one <ul>
 * with different contracts, one cancellable and one gone the instant it was
 * touched, with no confirmation and no undo (2026-09-06 visual pass).
 *
 * This dialog is the ONLY place the control may live. ACLs are portal-gated: an
 * app can ask for sharing via `host.requestShare`, which opens this trusted
 * chrome over the app's own project, so every app inherits invitations with no
 * bundle change. A people picker inside an untrusted app surface is never
 * acceptable, however convenient.
 */

import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import {
  Link2,
  Lock,
  Loader2,
  X,
  UserPlus,
  Search,
  Check,
  Copy,
  Trash2,
  Play,
  Mail,
  MailX,
} from 'lucide-react';
import { PopoverPortal } from '@/components/ui/popover-portal';
import { useAuth } from '@/components/auth-provider';
import { useSignupGate } from '@/components/workspace/signup-gate';
import { isGuestUser } from '@/lib/auth/is-guest';
import { cn } from '@/lib/utils';

export type ShareVisibility = 'private' | 'unlisted' | 'public';
export type ShareKind = 'content' | 'workspace';

/** An app-declared TYPED share link the dialog offers alongside the default
 *  one (e.g. a deck's player link). The dialog mints it on the owner's
 *  session via the same share-link route; declaring a type grants nothing. */
export interface ShareLinkTypeSpec {
  type: string;
  label: string;
  hint?: string;
}

export type ShareDialogProps =
  | {
      kind: 'content';
      contentIds: string[];
      onClose: () => void;
      onSaved?: (result: { updatedCount: number; cascadedCount: number }) => void;
      /** Extra typed links to offer (single-content shares only). */
      linkTypes?: ShareLinkTypeSpec[];
    }
  | {
      kind: 'workspace';
      workspaceId: string;
      onClose: () => void;
      onSaved?: () => void;
    };

/**
 * The dialog's role vocabulary. 'suggester' (read, comment and propose tracked
 * changes an editor reviews) is offered only where the app honours it: the
 * grants summary says so with `suggestable` (lib/collab/suggest-mode.ts).
 */
type DialogRole = 'editor' | 'suggester' | 'viewer';
const dialogRoleOf = (role: string): DialogRole =>
  role === 'editor' ? 'editor' : role === 'suggester' ? 'suggester' : 'viewer';

interface GrantRow {
  userId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  role: 'shared' | 'editor' | 'viewer' | 'suggester';
  /** 'link' = joined by opening the owner's share link (private content);
   *  'invite' = joined by claiming an email invitation. */
  via?: 'link' | 'invite' | null;
}

/**
 * One row of `invites` from the grants route: the server's InviteSummary over
 * JSON, so the dates arrive as ISO strings. Only pending and claimed rows are
 * ever returned; a spent ticket is not the owner's business any more.
 */
interface InviteRow {
  id: string;
  email: string;
  role: 'shared' | 'editor' | 'suggester';
  status: string;
  expiresAt: string;
  delivery: string;
  deliveryReason: string | null;
  claimedByUserId: string | null;
  claimedAt: string | null;
  sendCount: number;
  createdAt: string;
}

/**
 * What one mint returned. `claimUrl` IS the ticket: the server hands it out
 * once, to the owner, and never again, so the card that shows it exists only
 * for the life of this dialog instance and is never reconstructed from a
 * reload of the summary.
 */
interface MintedInvite {
  inviteId: string;
  email: string;
  claimUrl: string;
  delivery: string;
  deliveryReason: string | null;
}

/** Loose shape check before the button enables. The server is the authority
 *  (it folds tags and dots and applies the caps); this only stops an obvious
 *  typo from costing a round trip. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface GrantSummary {
  visibility: ShareVisibility;
  grants: GrantRow[];
  /**
   * The item's display name, from the server (the grants route, already
   * access-gated), for the title "Share <name>". Never from the app that
   * asked for the dialog: this is trusted chrome and an app is not.
   */
  name?: string | null;
  /** Owner only. Non-owners get `[]`, exactly like `grants`. */
  invites?: InviteRow[];
  /**
   * False = the viewer can read the content but is not its owner (a share
   * recipient): the dialog renders read-only — links stay usable (recipients
   * re-share via their personal link), ACL management is hidden. Absent
   * (workspace / bulk / older responses) = true.
   */
  canManage?: boolean;
  descendantCount?: number;
  /** Content type — drives the share-link target (`widget` → `/w/{id}`). */
  contentType?: string | null;
  /**
   * True when the content opens full-screen in an app (`/w/…`): widgets and
   * ADR-0022 project folders (`metadata.projectMime`). Computed server-side
   * (lib/content/share-landing.ts) so the dialog never re-derives it.
   */
  appLanding?: boolean;
  /** The content opens in an app that honours suggest mode: offer Suggester. */
  suggestable?: boolean;
}

interface PendingGrant {
  userId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  /** This person's role, editable on their own row before Save. */
  role: DialogRole;
}

// Who-can-open choices for the link row's dropdown (B3 layout): the label IS
// the row title, so it reads as a sentence about the link, not an ACL term.
// Private links still work: opening one adds the person below (enrollment).
//
// TWO choices, not three. 'unlisted' and 'public' grant the SAME access:
// canAccessContent accepts either for anyone holding the URL. They differ only
// in whether LISTING surfaces show the item, and for documents no such surface
// exists (discovery covers agents / channels / posts / tags / users; the site
// search is the docs search; the profile lists no content). So the old third
// rung, "Anyone", read as more access than "Anyone with the link" while
// changing nothing a person could observe. Two rungs, one axis, no dead option.
// Labels and hints are translation keys (lib/i18n/translations/en.json,
// `share.vis*`). The private hint says "the people you invite", not "listed
// above": since the e2e run of 2026-09-26 this section sits ABOVE the people
// list (who can open it is the first decision; the list follows from it).
const VISIBILITY_OPTIONS = [
  { id: 'private', labelKey: 'share.visPrivate', icon: Lock, hintKey: 'share.visPrivateHint' },
  { id: 'open', labelKey: 'share.visOpen', icon: Link2, hintKey: 'share.visOpenHint' },
] as const;

/** The dropdown's two rungs mapped onto the three stored visibilities. */
type LinkAudience = 'private' | 'open';
const audienceOf = (v: ShareVisibility): LinkAudience => (v === 'private' ? 'private' : 'open');

export function ShareDialog(props: ShareDialogProps) {
  const t = useT();
  const isWorkspace = props.kind === 'workspace';
  const labelId = useId();

  // Guest gate: a guest can't own a persistent shareable ACL, so sharing is a
  // high-intent moment to convert. Instead of the grants UI, prompt signup
  // (upgrades the guest in place) and close. Central chokepoint — covers every
  // share entry point (widget Share, canvas node, library, workspace).
  const { user } = useAuth();
  const gate = useSignupGate();
  const isGuest = isGuestUser(user);

  const ids = isWorkspace ? [props.workspaceId] : props.contentIds;
  const summaryUrl =
    !isWorkspace && props.contentIds.length === 1
      ? `/api/content/${props.contentIds[0]}/grants`
      : isWorkspace
        ? `/api/workspaces/${props.workspaceId}/grants`
        : null;

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<GrantSummary | null>(null);
  const [visibility, setVisibility] = useState<ShareVisibility>('private');
  const [pendingAdds, setPendingAdds] = useState<PendingGrant[]>([]);
  const [pendingRemoves, setPendingRemoves] = useState<Set<string>>(new Set());
  // Withdrawals STAGE like every other removal in this list and commit on Save.
  // They used to POST immediately, which put two different contracts on two
  // trash icons in the same <ul>: one you could still cancel, one that was gone
  // the instant you touched it, with no confirmation and no undo. Minting stays
  // immediate because it returns a one-time link and can queue mail; a
  // withdrawal returns nothing and sends nothing, so there is no reason for it
  // to skip Save. Reported by the 2026-09-06 visual pass.
  const [pendingInviteRevokes, setPendingInviteRevokes] = useState<Set<string>>(new Set());
  // Per-existing-grantee role override (userId -> new tier), so the owner can
  // change someone's role inline instead of remove + re-add.
  const [roleEdits, setRoleEdits] = useState<Record<string, DialogRole>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Share-link section state (single-content only). For public content the
  // link is the bare canonical URL; for unlisted content we lazily mint a
  // `?t=` token (visibility-independent access) so the link works for anon
  // viewers even before/without Save persisting the visibility change.
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareLinkLoading, setShareLinkLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Email invitation state. `inviteRole` uses the dialog's Editor/Viewer
  // vocabulary and is mapped to the wire's 'editor'/'shared' at POST time.
  // `minted` holds the once-only claim links for this dialog session, newest
  // first; `withdrawing` is the set of invite ids with a revoke in flight.
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<DialogRole>('viewer');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [minted, setMinted] = useState<MintedInvite[]>([]);
  const [withdrawing, setWithdrawing] = useState<Set<string>>(new Set());

  const singleContentId =
    !isWorkspace && props.contentIds.length === 1 ? props.contentIds[0] : null;

  // Load summary. Bulk-content selections show a "mixed visibility" mode
  // and skip the grant list — too noisy to merge per-row state.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        if (summaryUrl) {
          const res = await fetch(summaryUrl);
          if (!res.ok) throw new Error(`Failed to load (${res.status})`);
          const data = (await res.json()) as GrantSummary;
          if (cancelled) return;
          setSummary(data);
          setVisibility(data.visibility);
        } else {
          // Bulk mode — no per-item visibility to surface.
          setSummary({ visibility: 'private', grants: [] });
          setVisibility('private');
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [summaryUrl]);

  const close = useCallback(() => props.onClose(), [props]);

  // On mount for a guest, prompt signup and dismiss (the render guard below
  // keeps the grants UI from flashing). `requireSignup` is a no-op outside the
  // workspace's SignupGateProvider, so this degrades safely.
  useEffect(() => {
    if (!isGuest) return;
    void gate.requireSignup({ explicit: true, reason: 'Sign up to share your work.' });
    props.onClose();
    // Run once when a guest is detected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuest]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  // Compute the share link whenever visibility / content type changes
  // (single-content only). Widgets AND project folders open full-screen in
  // their app at `/w/{id}` (server-computed `appLanding` flag); other content
  // uses `/content/{id}`. Public links are bare; unlisted links carry a
  // `?t=` token minted on the owner's link row so anon viewers can open
  // them regardless of the persisted visibility.
  useEffect(() => {
    // Do NOT mint for PRIVATE. The code is a CAPABILITY: whoever opens it is
    // admitted and enrolled as a viewer, so offering a copyable link under a
    // rung that says "only people I invite" handed out the opposite of what it
    // promised, and merely opening this dialog created that capability. Private
    // now offers no link, and switching TO private revokes the old one on save,
    // which is what makes the label true.
    if (!singleContentId || visibility === 'private') {
      setShareUrl(null);
      setCopied(false);
      return;
    }
    setCopied(false);
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const appLanding = summary?.contentType === 'widget' || summary?.appLanding === true;

    // Non-app public content: the canonical content URL needs no token.
    if (!appLanding && visibility === 'public') {
      setShareUrl(`${origin}/content/${singleContentId}`);
      return;
    }

    // Otherwise mint (or reuse) the short share code. For app-landing content
    // it IS the whole link — `/w/<code>` — giving a short, unguessable URL
    // with no path UUID. For unlisted non-app content it rides as `?t=<code>`.
    // Prefer the server-computed `url` (share-landing is decided in one place);
    // the client construction is only the fallback for older responses.
    let cancelled = false;
    setShareLinkLoading(true);
    fetch(`/api/content/${singleContentId}/share-link`, { method: 'POST' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { shareToken?: string; url?: string } | null) => {
        if (cancelled) return;
        const token = data?.shareToken;
        if (data?.url) {
          setShareUrl(data.url);
        } else if (appLanding) {
          // Short code-only link; fall back to the content id if minting
          // somehow returned no token (public widgets stay reachable).
          setShareUrl(token ? `${origin}/w/${token}` : `${origin}/w/${singleContentId}`);
        } else {
          setShareUrl(
            token
              ? `${origin}/content/${singleContentId}?t=${token}`
              : `${origin}/content/${singleContentId}`,
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setShareUrl(
            appLanding
              ? `${origin}/w/${singleContentId}`
              : `${origin}/content/${singleContentId}`,
          );
        }
      })
      .finally(() => {
        if (!cancelled) setShareLinkLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [singleContentId, visibility, summary?.contentType, summary?.appLanding]);

  const copyShareLink = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked (insecure context / no gesture) — leave the
      // input selectable so the user can copy manually.
    }
  }, [shareUrl]);

  const inviteEmailValid = EMAIL_SHAPE.test(inviteEmail.trim());

  // Mint one invitation now (not on Save; see the file-top block). The response
  // is the new authoritative `invites` list plus the once-only claim link,
  // which goes into `minted`. Re-inviting an address rotates its ticket, so an
  // older card for the same address is dropped: its link is dead.
  const sendInvite = async () => {
    if (!singleContentId || !inviteEmailValid || inviting) return;
    const email = inviteEmail.trim();
    setInviting(true);
    setInviteError(null);
    try {
      const res = await fetch(`/api/content/${singleContentId}/grants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invites: { add: [{ email, role: inviteRole === 'viewer' ? 'shared' : inviteRole }] },
        }),
      });
      const data = (await res.json().catch(() => null)) as
        | { invites?: InviteRow[]; minted?: MintedInvite[]; error?: string; code?: string }
        | null;
      if (!res.ok) {
        throw new Error(data?.error || t('share.inviteFailed'));
      }
      const fresh = (data?.minted ?? []).map((m) => ({
        inviteId: m.inviteId,
        email: m.email ?? email,
        claimUrl: m.claimUrl,
        delivery: m.delivery ?? 'not_sent',
        deliveryReason: m.deliveryReason ?? null,
      }));
      const replacedEmails = new Set(fresh.map((m) => m.email.toLowerCase()));
      setMinted((prev) => [
        ...fresh,
        ...prev.filter((m) => !replacedEmails.has(m.email.toLowerCase())),
      ]);
      setSummary((prev) =>
        prev ? { ...prev, invites: data?.invites ?? prev.invites ?? [] } : prev,
      );
      setInviteEmail('');
    } catch (err) {
      setInviteError((err as Error).message);
    } finally {
      setInviting(false);
    }
  };

  // Revoke one ticket now. The route may echo the refreshed `invites` list;
  // when it does not, the row is dropped locally, and either way the minted
  // card for that ticket goes with it because its link no longer opens.
  const withdrawInvite = (invite: InviteRow) => {
    setInviteError(null);
    setPendingInviteRevokes((prev) => new Set(prev).add(invite.id));
    // Drop the one-time link card too: the ticket is on its way out, so leaving
    // a copyable link on screen would invite someone to send a dead one.
    setMinted((prev) => prev.filter((m) => m.inviteId !== invite.id));
  };

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Inline role changes on existing grantees become re-grant "add" ops
      // (grantUsers upserts the role on the (content_id, user_id) unique index,
      // so a Viewer↔Editor flip is just a re-add with the new role).
      const roleChangeOps = (summary?.grants ?? [])
        .filter((g) => !pendingRemoves.has(g.userId))
        .map((g) => {
          const current = dialogRoleOf(g.role);
          const next = roleEdits[g.userId];
          return next && next !== current ? { userId: g.userId, role: next } : null;
        })
        .filter((x): x is { userId: string; role: DialogRole } => x != null);
      const addOps = [
        ...pendingAdds.map((p) => ({ userId: p.userId, role: p.role })),
        ...roleChangeOps,
      ];
      const removeOps = Array.from(pendingRemoves).map((userId) => ({ userId }));
      const visibilityChanged =
        !!summaryUrl && summary != null && summary.visibility !== visibility;

      if (isWorkspace) {
        const body: Record<string, unknown> = {};
        if (visibilityChanged || !summaryUrl) body.visibility = visibility;
        if (addOps.length > 0 || removeOps.length > 0) {
          body.grants = { add: addOps, remove: removeOps };
        }
        if (Object.keys(body).length === 0) {
          close();
          return;
        }
        const res = await fetch(`/api/workspaces/${props.workspaceId}/grants`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `Save failed (${res.status})`);
        }
        props.onSaved?.();
      } else if (ids.length === 1) {
        const body: Record<string, unknown> = {};
        if (visibilityChanged) body.visibility = visibility;
        if (addOps.length > 0 || removeOps.length > 0) {
          body.grants = { add: addOps, remove: removeOps };
        }
        const revokeOps = Array.from(pendingInviteRevokes).map((inviteId) => ({ inviteId }));
        if (revokeOps.length > 0) body.invites = { revoke: revokeOps };
        if (Object.keys(body).length === 0) {
          close();
          return;
        }
        const res = await fetch(`/api/content/${ids[0]}/grants`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`Save failed (${res.status})`);
        // Going back to invite-only retires the code that was handed out. The
        // people it already admitted keep their grants and stay in the list
        // above, individually removable; the LINK stops working. Best-effort:
        // the visibility change is the load-bearing part and has landed.
        if (visibilityChanged && visibility === 'private') {
          await fetch(`/api/content/${ids[0]}/share-link`, { method: 'DELETE' }).catch(() => {});
        }
        const data = await res.json();
        props.onSaved?.({ updatedCount: 1, cascadedCount: data.cascadedCount ?? 0 });
      } else {
        // Bulk content path. Visibility is always applied (defaults to
        // current 'private' if untouched is fine — bulk mode is opt-in
        // to changing visibility on the whole selection).
        const body: Record<string, unknown> = {
          contentIds: ids,
        };
        if (!summaryUrl) body.visibility = visibility;
        if (visibilityChanged && summaryUrl) body.visibility = visibility;
        // For bulk (no summaryUrl), only include visibility if user touched
        // it or grants were added/removed; otherwise nothing to do.
        if (addOps.length > 0 || removeOps.length > 0) {
          body.grants = { add: addOps, remove: removeOps };
        }
        if (!body.visibility && !body.grants) {
          close();
          return;
        }
        const res = await fetch('/api/content/batch/grants', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `Bulk save failed (${res.status})`);
        }
        const data = (await res.json()) as {
          updated: number;
          cascadedCount: number;
        };
        props.onSaved?.({
          updatedCount: data.updated,
          cascadedCount: data.cascadedCount,
        });
      }
      close();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const grants = summary?.grants ?? [];
  const visibleGrants = grants.filter((g) => !pendingRemoves.has(g.userId));
  // Read-only mode for share recipients (canManage:false from the grants
  // route). Everything that mutates access is hidden; the link rows stay,
  // because the share-link route mints per-user links for recipients too.
  const canManage = summary?.canManage !== false;

  // Invitations are a single-content, owner-only affordance: the route mints
  // them for content the actor manages, and workspaces and bulk selections keep
  // the account picker alone.
  const canInviteByEmail = canManage && !!singleContentId;
  // Suggester is a per-document tier: offered only for one document that opens
  // in an app honouring suggest mode (the server decides), never for a
  // workspace or a bulk selection, where it would read exactly like Viewer.
  const suggestable = !isWorkspace && !!singleContentId && summary?.suggestable === true;
  const invites = summary?.invites ?? [];
  // A claimed ticket became an ordinary grant row for the claimant, so it is
  // shown as a note on THAT row ("Invited as sam@acme.com") rather than as a
  // second row for the same person. Pending tickets get their own row.
  const claimedByUser = new Map<string, InviteRow>();
  for (const i of invites) {
    if (i.status === 'claimed' && i.claimedByUserId) claimedByUser.set(i.claimedByUserId, i);
  }
  const grantUserIds = new Set(visibleGrants.map((g) => g.userId));
  const pendingInvites = invites.filter((i) => i.status === 'pending');
  // Claimed, but the claimant is no longer in the list (removed after the
  // claim): still shown, still withdrawable, so nothing is silently hidden.
  const orphanClaimedInvites = invites.filter(
    (i) => i.status === 'claimed' && !(i.claimedByUserId && grantUserIds.has(i.claimedByUserId)),
  );
  const inviteRows = [...pendingInvites, ...orphanClaimedInvites].filter(
    (i) => !pendingInviteRevokes.has(i.id),
  );

  // The title names the thing when the server told us its name; a bare
  // "Share item" left the person unsure what they were about to share.
  const namedTitle = summary?.name ? t('share.titleNamed', { name: summary.name }) : null;
  const titleText = isWorkspace
    ? (namedTitle ?? t('share.titleWorkspace'))
    : ids.length === 1
      ? (namedTitle ?? t('share.titleItem'))
      : t('share.titleItems', { count: ids.length });

  // Guests never see the grants UI — the effect above redirects them to signup.
  if (isGuest) return null;

  return (
    <PopoverPortal
      role="dialog"
      ariaLabel="Share"
      // Where the panel sits is decided by CLASSES, not by the inline style:
      // a bottom sheet on a phone (`items-end`), a card near the top on a
      // desktop (`md:items-start`). The alignment used to be inline as well
      // (`alignItems: 'flex-end'`), and an inline style beats a class, so the
      // desktop rule never applied and the dialog sat at the bottom edge of
      // the page (the Revent run on dev, 2026-10-02).
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 240,
        display: 'flex',
        justifyContent: 'center',
      }}
      className="items-end bg-black/40 backdrop-blur-sm md:items-start md:py-[10vh]"
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className={cn(
          'glass-panel relative flex w-full flex-col overflow-hidden shadow-2xl',
          // Mobile: bottom sheet pinned to viewport bottom, max 90vh, rounded top corners only
          'max-h-[90vh] rounded-t-2xl',
          // Desktop: centered card up to 480px wide, fully rounded
          'md:max-h-[80vh] md:w-[min(480px,96vw)] md:rounded-2xl',
        )}
        data-testid="share-dialog"
      >
        <div className="flex shrink-0 items-center gap-2 px-4 py-3 shadow-[inset_0_-1px_0_rgba(15,23,42,0.06)] dark:shadow-[inset_0_-1px_0_rgba(0,0,0,0.35)]">
          <h2 id={labelId} className="min-w-0 truncate text-sm font-semibold tracking-tight" data-testid="share-title">
            {titleText}
          </h2>
          <div className="ml-auto" />
          <button
            type="button"
            aria-label={t('workspace.canvas.node.close')}
            onClick={close}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : (
            <div className="space-y-5">
              {!canManage && (
                <p
                  className="rounded-md border border-border px-3 py-3 text-xs text-muted-foreground"
                  data-testid="share-readonly-note"
                >
                  Only the owner can change who has access.
                </p>
              )}
              {/* LINK GROUP (B3): one bordered stack. Row 1 = who-can-open
                  dropdown + hint + Copy for the default link; each app-declared
                  typed link is its own self-describing row with its own Copy.
                  The dropdown governs every link in the group. */}
              <section className="space-y-1.5" data-testid="share-link-section">
                <div className="rounded-md border border-border">
                  <div className="flex items-center gap-2.5 px-3 py-2.5">
                    {(() => {
                      // Link when anyone holding it can open, lock when invite-only.
                      const Icon = visibility === 'private' ? Lock : Link2;
                      return <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />;
                    })()}
                    <div className="min-w-0 flex-1">
                      {canManage ? (
                        <select
                          value={audienceOf(visibility)}
                          onChange={(e) => {
                            // Opening the link lands on UNLISTED. Listing it is a
                            // second, explicit decision (the switch below), never a
                            // side effect of handing someone a link.
                            const next = e.target.value as LinkAudience;
                            setVisibility(next === 'private' ? 'private' : 'unlisted');
                          }}
                          className="-ml-1 h-7 max-w-full rounded-md bg-transparent pl-1 pr-6 text-xs font-medium text-foreground transition-colors hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                          aria-label={t('share.whoCanOpen')}
                          data-testid="share-visibility-select"
                        >
                          {VISIBILITY_OPTIONS.map((opt) => (
                            <option key={opt.id} value={opt.id}>{t(opt.labelKey)}</option>
                          ))}
                        </select>
                      ) : (
                        /* Read-only: the owner's setting as a statement, in the
                           viewer's voice ("I invite" would be the wrong I). */
                        <div className="flex h-7 items-center text-xs font-medium text-foreground">
                          {audienceOf(visibility) === 'private'
                            ? t('share.visPrivateReadonly')
                            : t('share.visOpen')}
                        </div>
                      )}
                    {/* What the link confers belongs in THIS line, not in a
                        third element beside Copy: the row has room for the
                        control and the button, and a floating "can view" both
                        repeated the line below it and squeezed the hint into
                        truncating mid-word. When comment and edit arrive
                        (docs/adr/0029) the row gets laid out again around a real
                        control; until then the sentence carries it.
                        No `truncate`: this copy varies, and wrapping to a second
                        line reads, while a clipped word does not. */}
                      <p className="text-xs leading-snug text-muted-foreground">
                        {canManage
                          ? t(
                              VISIBILITY_OPTIONS.find((o) => o.id === audienceOf(visibility))?.hintKey ??
                                'share.visOpenHint',
                            )
                          : audienceOf(visibility) === 'private'
                            ? t('share.visPrivateReadonlyHint')
                            : t('share.visOpenHint')}
                        {visibility !== 'private' && summary?.contentType === 'widget' && ' Opens full screen.'}
                      </p>
                    </div>
                    {singleContentId && visibility !== 'private' && (
                      <button
                        type="button"
                        onClick={copyShareLink}
                        disabled={!shareUrl || shareLinkLoading}
                        className="inline-flex h-8 shrink-0 items-center justify-center gap-1 rounded-md border border-border px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
                        data-testid="share-link-copy"
                        aria-label={t('posts.copyLink')}
                      >
                        {copied ? (
                          <>
                            <Check className="h-3.5 w-3.5" /> Copied
                          </>
                        ) : (
                          <>
                            <Copy className="h-3.5 w-3.5" /> Copy
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  {/* Same private gate as the default link row: a typed link is
                      still a link, and under "only invited people" the dialog
                      must offer none (an owner's code is a capability; a
                      recipient's would be dead on arrival). */}
                  {singleContentId &&
                    visibility !== 'private' &&
                    (props.kind === 'content' ? props.linkTypes : undefined)?.map((spec) => (
                      <TypedLinkRow key={spec.type} contentId={singleContentId} spec={spec} />
                    ))}
                </div>
              </section>

              {canManage && (
              <section className="space-y-2">
                {/* No section-level role control. It set the role for the NEXT
                    person added, sat beside a per-person control that sets an
                    existing person's role, and used the same widget for both, so
                    the header could read "Viewer" while the row under it read
                    "Editor" with neither wrong. Roles now live only on the row
                    they govern; a pending row is editable, which is what the
                    header control existed to work around. */}
                <div className="text-xs font-medium text-muted-foreground">{t('share.peopleWithAccess')}</div>
                <UserPicker
                  excludeIds={[
                    ...visibleGrants.map((g) => g.userId),
                    ...pendingAdds.map((p) => p.userId),
                  ]}
                  onPick={(p) =>
                    setPendingAdds((prev) => [
                      ...prev,
                      // Workspaces invite collaborators, files invite readers.
                      { ...p, role: isWorkspace ? 'editor' : 'viewer' },
                    ])
                  }
                />
                {canInviteByEmail && (
                  /* Email invitation: address + role + Invite. `noValidate`
                     because the button gates on EMAIL_SHAPE and the server is
                     the authority; the browser's own tooltip would be a third
                     voice. Enter submits, like the search above. */
                  <form
                    noValidate
                    onSubmit={(e) => {
                      e.preventDefault();
                      void sendInvite();
                    }}
                    // flex-wrap is load-bearing, not decoration. The validation
                    // hint below is `basis-full`, which only drops to its own
                    // line inside a WRAPPING row; in a nowrap row it becomes a
                    // fourth item claiming the full width, and the `min-w-0
                    // flex-1` input shrinks to 38px to make room, hiding what
                    // the person just typed and clipping "Viewer" to "wer".
                    // Found by the 2026-09-06 visual pass.
                    className="flex flex-wrap items-center gap-2"
                    data-testid="share-invite-form"
                  >
                    <div className="relative min-w-0 flex-1">
                      <Mail className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="email"
                        inputMode="email"
                        autoComplete="off"
                        spellCheck={false}
                        value={inviteEmail}
                        onChange={(e) => {
                          setInviteEmail(e.target.value);
                          if (inviteError) setInviteError(null);
                        }}
                        placeholder={t('share.inviteEmailPlaceholder')}
                        aria-label={t('share.inviteByEmail')}
                        className="h-9 w-full rounded-md border border-border bg-background pl-7 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40"
                        data-testid="share-invite-email"
                      />
                    </div>
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value as DialogRole)}
                      className="h-9 shrink-0 rounded-md border border-border bg-background px-1.5 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                      aria-label={t('share.inviteRoleAria')}
                      data-testid="share-invite-role"
                    >
                      <option value="editor">{t('share.roleEditor')}</option>
                      {suggestable && <option value="suggester">{t('share.roleSuggester')}</option>}
                      <option value="viewer">{t('share.roleViewer')}</option>
                    </select>
                    <button
                      type="submit"
                      disabled={!inviteEmailValid || inviting}
                      className="inline-flex h-9 shrink-0 items-center justify-center gap-1 rounded-md border border-border px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
                      data-testid="share-invite-send"
                    >
                      {inviting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t('share.sendInvite')}
                    </button>
                    {inviteEmail.trim().length > 3 && !inviteEmailValid && (
                      <p
                        className="basis-full pt-1 text-[11px] text-muted-foreground"
                        data-testid="share-invite-hint"
                      >
                        {t('share.inviteEmailInvalid')}
                      </p>
                    )}
                  </form>
                )}
                {inviteError && (
                  <p className="text-xs text-destructive" role="alert" data-testid="share-invite-error">
                    {inviteError}
                  </p>
                )}
                {minted.map((m) => (
                  <MintedInviteCard key={m.inviteId} minted={m} />
                ))}
                <ul className="divide-y divide-border rounded-md border border-border">
                  {pendingAdds.length === 0 && visibleGrants.length === 0 && inviteRows.length === 0 && (
                    <li className="px-3 py-4 text-center text-xs text-muted-foreground">
                      {t('share.noPeopleYet')}
                    </li>
                  )}
                  {pendingAdds.map((p) => (
                    <GrantListItem
                      key={`pending-${p.userId}`}
                      avatarUrl={p.avatarUrl}
                      displayName={p.displayName ?? p.username}
                      username={p.username}
                      roleLabel={p.role}
                      suggestable={suggestable}
                      editableRole={{
                        value: p.role,
                        onChange: (r) =>
                          setPendingAdds((prev) =>
                            prev.map((x) => (x.userId === p.userId ? { ...x, role: r } : x)),
                          ),
                      }}
                      pending
                      onRemove={() =>
                        setPendingAdds((prev) => prev.filter((x) => x.userId !== p.userId))
                      }
                    />
                  ))}
                  {visibleGrants.map((g) => (
                    <GrantListItem
                      key={g.userId}
                      avatarUrl={g.avatarUrl}
                      displayName={g.displayName ?? g.username}
                      username={g.username}
                      roleLabel={g.role}
                      viaLink={g.via === 'link'}
                      note={
                        claimedByUser.has(g.userId)
                          ? t('share.invitedAs', { email: claimedByUser.get(g.userId)!.email })
                          : g.via === 'invite'
                            ? t('share.joinedViaInvite')
                            : undefined
                      }
                      suggestable={suggestable}
                      editableRole={{
                        value: roleEdits[g.userId] ?? dialogRoleOf(g.role),
                        onChange: (r) => setRoleEdits((prev) => ({ ...prev, [g.userId]: r })),
                      }}
                      onRemove={() =>
                        setPendingRemoves((prev) => {
                          const next = new Set(prev);
                          next.add(g.userId);
                          return next;
                        })
                      }
                    />
                  ))}
                  {inviteRows.map((i) => (
                    <InviteListItem
                      key={`invite-${i.id}`}
                      invite={i}
                      withdrawing={withdrawing.has(i.id)}
                      onWithdraw={() => withdrawInvite(i)}
                    />
                  ))}
                </ul>
              </section>
              )}

              {!isWorkspace && ids.length > 1 && (
                <p className="text-xs text-muted-foreground">
                  Changes apply to all {ids.length} selected items you own.
                  Items you don&apos;t own are skipped.
                </p>
              )}

              {error && (
                <p className="text-xs text-destructive" role="alert">
                  {error}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 px-4 py-3 shadow-[inset_0_1px_0_rgba(15,23,42,0.06)] dark:shadow-[inset_0_1px_0_rgba(0,0,0,0.4)]">
          {canManage ? (
            <>
              <button
                type="button"
                onClick={close}
                className="rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onSave}
                disabled={saving || loading}
                className="inline-flex items-center justify-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
                data-testid="share-save"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
              </button>
            </>
          ) : (
            /* Read-only: nothing to save, so a single dismiss. */
            <button
              type="button"
              onClick={close}
              className="inline-flex items-center justify-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              data-testid="share-done"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </PopoverPortal>
  );
}

/** Switch, same geometry and states as the one in Settings. */
function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
        checked ? 'bg-primary' : 'bg-foreground/15',
      )}
    >
      <span
        className={cn(
          'inline-block transform rounded-full bg-background shadow-sm transition-transform',
          checked ? 'translate-x-[1.4rem]' : 'translate-x-1',
        )}
        style={{ width: '1.125rem', height: '1.125rem' }}
      />
    </button>
  );
}

/**
 * One app-declared TYPED link ("Player link", …): mints its own short code
 * via the share-link route (`{ type }`) on the caller's own session and
 * offers copy — a sibling of the default link row, and gated like it: the
 * rows render only while the content is link-shareable (not private).
 */
function TypedLinkRow({ contentId, spec }: { contentId: string; spec: ShareLinkTypeSpec }) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`/api/content/${contentId}/share-link`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ type: spec.type }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { url?: string } | null) => {
        if (!cancelled) setUrl(data?.url ?? null);
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [contentId, spec.type]);

  const copy = useCallback(async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the input stays selectable */
    }
  }, [url]);

  return (
    <div
      className="flex items-center gap-2.5 border-t border-border px-3 py-2.5"
      data-testid={`share-link-typed-${spec.type}`}
    >
      <Play className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-foreground">{spec.label}</div>
        <p className="truncate text-xs text-muted-foreground">
          {loading ? 'Generating link…' : url ? (spec.hint ?? '') : 'Link unavailable right now.'}
        </p>
      </div>
      <button
        type="button"
        onClick={copy}
        disabled={!url || loading}
        className="inline-flex h-8 shrink-0 items-center justify-center gap-1 rounded-md border border-border px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
        aria-label={`Copy ${spec.label.toLowerCase()}`}
      >
        {copied ? (
          <>
            <Check className="h-3.5 w-3.5" /> Copied
          </>
        ) : (
          <>
            <Copy className="h-3.5 w-3.5" /> Copy
          </>
        )}
      </button>
    </div>
  );
}

/**
 * The once-only claim link, with THE HONEST DELIVERY LINE above it. The line
 * states what the mail rail actually did with this ticket, and nothing else:
 * "sent" only when the rail accepted it ('queued'); otherwise the owner is told
 * plainly that no message went out and that the link is theirs to deliver.
 * Today the platform mail switch is off ('not_sent' / 'email_off'), so the
 * not-sent state is the one people see, and it is styled as a normal state,
 * not an error: nothing failed, the product simply does not send yet.
 */
function MintedInviteCard({ minted }: { minted: MintedInvite }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const sent = minted.delivery === 'queued';
  const line = sent
    ? t('share.inviteSent', { email: minted.email })
    : minted.delivery === 'suppressed'
      ? t('share.inviteSuppressed', { email: minted.email })
      : minted.deliveryReason === 'email_off'
        ? t('share.inviteEmailOff', { email: minted.email })
        : minted.deliveryReason === 'not_configured'
          ? t('share.inviteNoMailer', { email: minted.email })
          : t('share.inviteNotEmailed', { email: minted.email });
  const Icon = sent ? Mail : MailX;

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(minted.claimUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the input stays selectable */
    }
  }, [minted.claimUrl]);

  return (
    <div
      className="space-y-2 rounded-md border border-border px-3 py-2.5"
      data-testid="share-invite-minted"
      data-delivery={minted.delivery}
    >
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <p className="text-xs leading-snug text-foreground">{line}</p>
      </div>
      <div className="flex items-center gap-2">
        <input
          readOnly
          value={minted.claimUrl}
          onFocus={(e) => e.currentTarget.select()}
          aria-label={t('share.inviteLinkAria', { email: minted.email })}
          className="h-8 min-w-0 flex-1 rounded-md border border-border bg-background px-2 text-xs text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40"
          data-testid="share-invite-link"
        />
        <button
          type="button"
          onClick={copy}
          className="inline-flex h-8 shrink-0 items-center justify-center gap-1 rounded-md border border-border px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-accent"
          aria-label={t('share.copyInviteLink')}
          data-testid="share-invite-copy"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5" /> Copied
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" /> Copy
            </>
          )}
        </button>
      </div>
    </div>
  );
}

/**
 * A ticket that has not turned into a grant row: pending, or claimed by a
 * person who has since been removed. Sits in the same list as the people,
 * subtitled with its state. The role is fixed at mint (a ticket carries no
 * role edit), so it renders as the static badge, never the select.
 */
function InviteListItem({
  invite,
  withdrawing,
  onWithdraw,
}: {
  invite: InviteRow;
  withdrawing: boolean;
  onWithdraw: () => void;
}) {
  const t = useT();
  const subtitle =
    invite.status === 'claimed'
      ? t('share.inviteAccepted')
      : invite.delivery === 'queued'
        ? t('share.invitedNotOpened')
        : t('share.invitedNotEmailed');
  return (
    <li className="flex items-center gap-2 px-3 py-2" data-testid="share-invite-row">
      <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-muted text-muted-foreground">
        <Mail className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{invite.email}</div>
        <div className="truncate text-xs text-muted-foreground">{subtitle}</div>
      </div>
      <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
        {invite.role === 'editor'
          ? t('share.roleEditor')
          : invite.role === 'suggester'
            ? t('share.roleSuggester')
            : t('share.roleViewer')}
      </span>
      <button
        type="button"
        onClick={onWithdraw}
        disabled={withdrawing}
        className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-destructive disabled:pointer-events-none disabled:opacity-50"
        aria-label={t('share.withdrawInviteAria', { email: invite.email })}
        title={t('share.withdrawInvite')}
        data-testid="share-invite-withdraw"
      >
        {withdrawing ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Trash2 className="h-3.5 w-3.5" />
        )}
      </button>
    </li>
  );
}

function GrantListItem({
  avatarUrl,
  displayName,
  username,
  roleLabel,
  viaLink,
  note,
  editableRole,
  suggestable,
  pending,
  onRemove,
}: {
  avatarUrl: string | null;
  displayName: string;
  username: string;
  roleLabel: string;
  /** True when this person joined by opening the owner's share link. */
  viaLink?: boolean;
  /** Extra provenance beside the handle, e.g. "Invited as sam@acme.com". */
  note?: string;
  /** When set, render an inline role select instead of a static label
   *  so the owner can change an existing grantee's role without remove + re-add. */
  editableRole?: { value: DialogRole; onChange: (r: DialogRole) => void };
  /** Offer Suggester (the content opens in an app that honours it). */
  suggestable?: boolean;
  pending?: boolean;
  onRemove: () => void;
}) {
  const t = useT();
  return (
    <li className="flex items-center gap-2 px-3 py-2">
      <Avatar src={avatarUrl} alt={displayName} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{displayName}</div>
        <div className="truncate text-xs text-muted-foreground">
          @{username}
          {viaLink && <span className="ml-1.5 rounded bg-muted px-1 py-px text-[10px] text-muted-foreground">joined via link</span>}
          {note && <span className="ml-1.5 rounded bg-muted px-1 py-px text-[10px] text-muted-foreground">{note}</span>}
        </div>
      </div>
      {editableRole ? (
        <select
          value={editableRole.value}
          onChange={(e) => editableRole.onChange(e.target.value as DialogRole)}
          className="h-7 rounded-md border border-border bg-background px-1.5 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
          aria-label={`Role for ${displayName}`}
        >
          <option value="editor">{t('share.roleEditor')}</option>
          {(suggestable || editableRole.value === 'suggester') && (
            <option value="suggester">{t('share.roleSuggester')}</option>
          )}
          <option value="viewer">{t('share.roleViewer')}</option>
        </select>
      ) : (
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
          {roleLabel}
        </span>
      )}
      {pending && (
        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
          New
        </span>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-destructive"
        aria-label={`Remove ${displayName}`}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}

function Avatar({ src, alt }: { src: string | null; alt: string }) {
  if (src) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img src={src} alt={alt} className="h-7 w-7 rounded-full object-cover" />
    );
  }
  const initial = alt?.[0]?.toUpperCase() ?? '?';
  return (
    <div className="grid h-7 w-7 place-items-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
      {initial}
    </div>
  );
}

interface PickerResult {
  id: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
}

function UserPicker({
  excludeIds,
  onPick,
}: {
  excludeIds: string[];
  onPick: (p: Omit<PendingGrant, 'role'>) => void;
}) {
  const t = useT();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PickerResult[]>([]);
  const [loading, setLoading] = useState(false);
  const exclude = useMemo(() => new Set(excludeIds), [excludeIds]);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        // `quick=1` = the indexed name lookup only. The full discovery
        // pipeline (Milvus + embedding + rerank) made every keystroke of a
        // handle search pay hundreds of ms for semantics that cannot beat a
        // name match on a name query.
        const res = await fetch(
          `/api/discovery/agents?type=human&search=${encodeURIComponent(term)}&limit=10&quick=1`,
        );
        if (!res.ok) {
          if (!cancelled) setResults([]);
          return;
        }
        // The discovery endpoint returns matches under `agents` (not `items`);
        // reading the wrong key silently yielded "No matches" for every search,
        // so people-sharing was effectively broken. Accept `agents`, with an
        // `items` fallback in case the endpoint shape changes.
        const data = (await res.json()) as { agents?: PickerResult[]; items?: PickerResult[] };
        if (cancelled) return;
        setResults(data.agents ?? data.items ?? []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query]);

  const filtered = results.filter((r) => !exclude.has(r.id));

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('share.searchPeople')}
          className="h-9 w-full rounded-md border border-border bg-background pl-7 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40"
          data-testid="share-user-search"
        />
        {loading && (
          <Loader2 className="absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>
      {filtered.length > 0 && (
        <ul className="max-h-40 divide-y divide-border overflow-y-auto rounded-md border border-border">
          {filtered.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => {
                  onPick({
                    userId: r.id,
                    username: r.username,
                    displayName: r.displayName,
                    avatarUrl: r.avatarUrl,
                  });
                  setQuery('');
                  setResults([]);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-accent"
              >
                <Avatar src={r.avatarUrl} alt={r.displayName ?? r.username} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">
                    {r.displayName ?? r.username}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">@{r.username}</div>
                </div>
                <UserPlus className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {query.trim().length >= 2 && !loading && filtered.length === 0 && (
        <div className="rounded-md border border-dashed border-border px-3 py-2 text-center text-xs text-muted-foreground">
          {t('share.noMatches')}
        </div>
      )}
    </div>
  );
}

// Surfaces a static marker so tests can detect the dialog regardless of role string.
ShareDialog.displayName = 'ShareDialog';
export { Check };
